import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import KitcoLogo from './KitcoLogo';
import CoinRecLogo from './CoinRecLogo';
import { 
  Smartphone, 
  Scan, 
  TrendingUp, 
  Shield, 
  Star, 
  Download, 
  Search, 
  Coins,
  Eye,
  BarChart3,
  Users,
  CheckCircle,
  ArrowRight,
  Play,
  Send,
  MessageCircle
} from 'lucide-react';

const LandingPage = () => {
  // 🔒 SIMPLE TRAFFIC PROTECTION: Set to true to block public access
  const BLOCK_PUBLIC_ACCESS = false;
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [accessUnlocked, setAccessUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  // Premium scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    // Observe all elements with animate-on-scroll class
    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          subject: contactForm.subject,
          category: contactForm.category,
          message: contactForm.message,
        }),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for your message! We will get back to you soon.');
        setContactForm({ name: '', email: '', subject: '', category: '', message: '' });
      } else {
        const error = await response.json();
        setSubmitMessage(error.detail || 'There was an error sending your message. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('There was an error sending your message. Please try again or contact mobilesupport@kitco.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactChange = (field) => (e) => {
    const value = e.target ? e.target.value : e; // Handle both input and select components
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Simple access check
  if (BLOCK_PUBLIC_ACCESS && !accessUnlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #1B222A 0%, #151A1F 50%, #1B222A 100%)'}}>
        <div className="max-w-sm w-full mx-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">CoinRec Preview</h1>
            <p className="text-slate-400">Enter access code to continue</p>
          </div>
          <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Access code"
              className="w-full px-4 py-3 mb-4 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  if (passwordInput === 'CoinRec2025') {
                    setAccessUnlocked(true);
                  } else {
                    alert('Invalid access code');
                    setPasswordInput('');
                  }
                }
              }}
            />
            <button
              onClick={() => {
                if (passwordInput === 'CoinRec2025') {
                  setAccessUnlocked(true);
                } else {
                  alert('Invalid access code');
                  setPasswordInput('');
                }
              }}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold py-3 rounded-lg transition-colors"
            >
              Access Site
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1B222A 0%, #151A1F 50%, #1B222A 100%)'}}>
      {/* Header */}
      <header className="fixed top-0 w-full backdrop-blur-md border-b border-slate-700/50 z-50" style={{backgroundColor: 'rgba(27, 34, 42, 0.9)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <KitcoLogo className="h-8" />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-slate-300 hover:text-amber-400 transition-colors font-medium">How It Works</a>
              <a href="#testimonials" className="text-slate-300 hover:text-amber-400 transition-colors font-medium">Reviews</a>
              <a href="https://kitco.com" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-amber-400 transition-colors font-medium">Back to Kitco.com</a>
              <Button 
                onClick={() => document.getElementById('support').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-900 px-6 font-semibold"
              >
                Contact Us
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section with Premium Animations */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden hero-tech tech-grid">
        <div className="absolute inset-0 opacity-90" style={{background: 'linear-gradient(135deg, #1B222A 0%, #151A1F 100%)'}}></div>
        
        {/* Floating Particles */}
        <div className="floating-particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6 animate-on-scroll">
              <CoinRecLogo className="h-24 sm:h-30 lg:h-36 text-amber-400" />
            </div>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tech-pulse"
              style={{
                animation: 'float 6s ease-in-out infinite',
                textShadow: '0 0 20px rgba(245, 158, 11, 0.3)'
              }}
            >
              Identify Coins Instantly
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed animate-on-scroll">
              Unlock the true value of your coins with CoinRec—the ultimate coin recognition app! Scan silver, gold, and precious metals for instant real-time market prices, or dive into detailed histories and descriptions for over 300,000 global coins. Build your portfolio effortlessly, track performance in real time, and create custom wish lists to expand your collection with precision and ease. Download now and elevate your numismatic journey!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Button 
                size="lg" 
                className="btn-ios px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all duration-500 transform hover:scale-110 hover:shadow-2xl hover:shadow-amber-500/25" 
                onClick={() => window.open('https://apps.apple.com/ca/app/coinrec/id1526904207', '_blank')}
                style={{
                  background: 'linear-gradient(45deg, #F59E0B, #D97706)',
                  border: 'none'
                }}
              >
                <Download className="w-5 h-5 mr-2" />
                Download for iOS
              </Button>
              <Button 
                size="lg" 
                className="btn-android px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-500 transform hover:scale-110 hover:shadow-2xl hover:shadow-green-500/25" 
                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.kitco.coinrec', '_blank')}
                style={{
                  background: 'linear-gradient(45deg, #10B981, #059669)',
                  border: 'none'
                }}
              >
                <Download className="w-5 h-5 mr-2" />
                Download for Android
              </Button>
            </div>
            
            {/* QR Code - Single Universal with Warm Hover Effect */}
            <div className="flex justify-center items-center mb-16">
              <div className="flex flex-col items-center">
                <div 
                  className="w-32 h-32 flex items-center justify-center mb-4 qr-glow scan-line tech-pulse qr-container" 
                  style={{
                    border: '3px solid #F59E0B', 
                    borderRadius: '16px', 
                    padding: '8px', 
                    boxShadow: '0 0 20px rgba(245, 158, 11, 0.5)',
                    animation: 'buttonPulse 3s ease-in-out infinite'
                  }}
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_coinrec-landing-1/artifacts/0s90425d_MOBILE-APP-LINKS%202025-10-07.png"
                    alt="Download CoinRec App"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="qr-text">
                  <p className="text-sm text-slate-300 font-medium text-center">Scan to Download</p>
                  <p className="text-xs text-slate-400 text-center">Detects your device automatically</p>
                </div>
              </div>
            </div>

            {/* App Screenshots Carousel - Simplified to ensure all images show */}
            <div className="relative max-w-7xl mx-auto">
              <div className="overflow-hidden">
                <div className="flex space-x-6 animate-scroll">
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/ebir2rjq_Takethephoto.png" alt="Take Photo of Coin" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/0qlthdat_Sendthephoto.png" alt="Send Photo for Analysis" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/v3ib7wul_Allinfo.png" alt="Complete Coin Details" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/lupjje41_GetResponse.png" alt="AI Recognition Results" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/s1lz842k_Collection.png" alt="Your Coin Collection" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/xrmd3238_Portfolio.png" alt="Portfolio Value Tracking" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/vdnewrji_MetalsPrice.png" alt="Kitco Metals Prices" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/9ngi9vpr_Currencies.png" alt="Currency Exchange Rates" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  
                  {/* Duplicate for loop */}
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/ebir2rjq_Takethephoto.png" alt="Take Photo of Coin" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/0qlthdat_Sendthephoto.png" alt="Send Photo for Analysis" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/v3ib7wul_Allinfo.png" alt="Complete Coin Details" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/lupjje41_GetResponse.png" alt="AI Recognition Results" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/s1lz842k_Collection.png" alt="Your Coin Collection" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/xrmd3238_Portfolio.png" alt="Portfolio Value Tracking" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/vdnewrji_MetalsPrice.png" alt="Kitco Metals Prices" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                  <img src="https://customer-assets.emergentagent.com/job_coinrec-tracker/artifacts/9ngi9vpr_Currencies.png" alt="Currency Exchange Rates" className="w-72 flex-shrink-0 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" />
                </div>
              </div>
              
              {/* Feature Labels Below Carousel */}
              <div className="mt-12 text-center">
                <h3 className="text-2xl font-bold text-white mb-8">Complete Coin Identification & Management Workflow</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                  <div className="text-center px-4">
                    <div className="w-16 h-16 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Scan className="w-8 h-8 text-slate-900" />
                    </div>
                    <h4 className="font-bold text-white text-lg mb-2">Instant Recognition</h4>
                    <p className="text-slate-300">Snap a photo and instantly identify coins and their precious metal content from our database of 300,000+ coins</p>
                  </div>
                  <div className="text-center px-4">
                    <div className="w-16 h-16 bg-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-slate-900" />
                    </div>
                    <h4 className="font-bold text-white text-lg mb-2">Live Precious Metal Prices</h4>
                    <p className="text-slate-300">Get real-time silver, gold, and platinum values to know exactly what your precious metal coins are worth</p>
                  </div>
                  <div className="text-center px-4">
                    <div className="w-16 h-16 bg-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BarChart3 className="w-8 h-8 text-slate-900" />
                    </div>
                    <h4 className="font-bold text-white text-lg mb-2">Collection Tracking</h4>
                    <p className="text-slate-300">Track your precious metal coin collection and monitor the total value of your silver and gold holdings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features section removed as requested */}

      {/* How It Works */}
      <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#1B222A'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-slate-300">Get started in three simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                icon: <Scan className="w-8 h-8" />,
                title: 'Take Photo',
                description: 'Simply snap a photo of your coin - whether it\'s a silver quarter, gold coin, or any precious metal piece'
              },
              {
                step: '2', 
                icon: <ArrowRight className="w-8 h-8" />,
                title: 'AI Analysis',
                description: 'Our AI instantly identifies your coin and determines its precious metal content and composition'
              },
              {
                step: '3',
                icon: <TrendingUp className="w-8 h-8" />,
                title: 'Get Live Value',
                description: 'See the current precious metal value based on live silver, gold, and platinum market prices'
              }
            ].map((item, index) => (
              <Card key={index} className="p-8 text-center hover:shadow-lg transition-shadow border-slate-700" style={{backgroundColor: '#151A1F'}}>
                <CardContent className="p-0">
                  <div className="w-16 h-16 bg-amber-400/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <div className="text-amber-400">{item.icon}</div>
                  </div>
                  <div className="text-sm font-bold text-amber-400 mb-2">STEP {item.step}</div>
                  <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                  <p className="text-slate-300">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#1B222A'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Complete Coin & Portfolio Management
            </h2>
            <p className="text-xl text-slate-300">Track, organize, and value your coin collection</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Coins className="w-8 h-8" />,
                title: 'My Collection',
                description: 'Track all your coins with detailed information, photos, and precious metal content'
              },
              {
                icon: <BarChart3 className="w-8 h-8" />,
                title: 'Portfolio Value',
                description: 'Real-time portfolio valuation based on current precious metal market prices'
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: 'Live Precious Metal Prices',
                description: 'Up-to-date silver, gold, platinum, and palladium prices with live market quotes'
              },
              {
                icon: <Eye className="w-8 h-8" />,
                title: 'Wishlist',
                description: 'Create and manage your wishlist of coins you want to add to your collection'
              },
              {
                icon: <MessageCircle className="w-8 h-8" />,
                title: 'FX Rates',
                description: 'Multi-currency support with live foreign exchange rates for global collectors'
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'Personal Notes & Photos',
                description: 'Store pictures and personal comments about your collection'
              }
            ].map((feature, index) => (
              <div 
                key={index} 
                className={`group animate-on-scroll stagger-${index + 1}`} 
                style={{ 
                  transition: 'all 0.4s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  const card = e.currentTarget.querySelector('.card-element');
                  const icon = e.currentTarget.querySelector('.icon-element');
                  const title = e.currentTarget.querySelector('.title-element');
                  
                  if (card) {
                    card.style.transform = 'translateY(-15px)';
                    card.style.boxShadow = '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(245,158,11,0.4)';
                    card.style.border = '2px solid rgb(245,158,11)';
                    card.style.backgroundColor = 'rgba(245,158,11,0.1)';
                  }
                  if (icon) {
                    icon.style.transform = 'scale(1.2) rotate(15deg)';
                    icon.style.backgroundColor = 'rgba(245,158,11,0.5)';
                  }
                  if (title) {
                    title.style.color = 'rgb(245,158,11)';
                    title.style.textShadow = '0 0 15px rgba(245,158,11,0.6)';
                  }
                }}
                onMouseLeave={(e) => {
                  const card = e.currentTarget.querySelector('.card-element');
                  const icon = e.currentTarget.querySelector('.icon-element');
                  const title = e.currentTarget.querySelector('.title-element');
                  
                  if (card) {
                    card.style.transform = 'translateY(0px)';
                    card.style.boxShadow = '';
                    card.style.border = '1px solid rgb(51, 65, 85)';
                    card.style.backgroundColor = '#151A1F';
                  }
                  if (icon) {
                    icon.style.transform = 'scale(1) rotate(0deg)';
                    icon.style.backgroundColor = 'rgba(245,158,11,0.2)';
                  }
                  if (title) {
                    title.style.color = 'white';
                    title.style.textShadow = 'none';
                  }
                }}
              >
                <Card 
                  className="card-element p-6 border-slate-700 premium-shadow" 
                  style={{
                    backgroundColor: '#151A1F',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <CardContent className="p-0">
                    <div 
                      className="icon-element w-12 h-12 bg-amber-400/20 rounded-lg flex items-center justify-center mb-4"
                      style={{ 
                        transition: 'all 0.3s ease',
                        transformOrigin: 'center'
                      }}
                    >
                      <div className="text-amber-400">{feature.icon}</div>
                    </div>
                    <h3 
                      className="title-element text-lg font-bold text-white mb-3"
                      style={{ transition: 'all 0.3s ease' }}
                    >
                      {feature.title}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#151A1F'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Trusted by Collectors Worldwide
            </h2>
            <div className="flex justify-center items-center space-x-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-amber-400 fill-current" />
              ))}
              <span className="ml-2 text-slate-300">4.9/5 on App Stores</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah J.',
                role: 'Coin Collector',
                content: 'I discovered my old silver quarters are worth much more than face value! CoinRec instantly showed me the live silver content and current precious metal value.',
                rating: 5
              },
              {
                name: 'Mike C.',
                role: 'Precious Metals Investor', 
                content: 'Perfect for tracking the precious metal content in my coin collection. I can see exactly how much silver, gold, or other metals I own and their current market value.',
                rating: 5
              },
              {
                name: 'Emma D.',
                role: 'Collector',
                content: 'Amazing how easy it is to get the real value of my precious metal coins! Just snap a photo and instantly see the silver or gold content worth.',
                rating: 5
              }
            ].map((testimonial, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow border-slate-700" style={{backgroundColor: '#1B222A'}}>
                <CardContent className="p-0">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-300 mb-4 italic">"{testimonial.content}"</p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-slate-400">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(90deg, #1B222A 0%, #151A1F 50%, #1B222A 100%)'}}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Discover Your Precious Metal Coin Values Today
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Uncover what your silver, gold, platinum, palladium, and rhodium coins—and more—are truly worth in real-time, across multiple currencies, with CoinRec. Start scanning and building your portfolio now!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button size="lg" className="btn-ios px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all duration-300" onClick={() => window.open('https://apps.apple.com/ca/app/coinrec/id1526904207', '_blank')}>
              <Download className="w-5 h-5 mr-2" />
              Download for iOS
            </Button>
            <Button size="lg" className="btn-android px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300" onClick={() => window.open('https://play.google.com/store/apps/details?id=com.kitco.coinrec', '_blank')}>
              <Download className="w-5 h-5 mr-2" />
              Download for Android
            </Button>
          </div>
          
          {/* QR Code - Second Instance with Warm Hover Effect */}
          <div className="flex justify-center items-center">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 flex items-center justify-center mb-4 qr-glow scan-line qr-container" style={{border: '3px solid #F59E0B', borderRadius: '16px', padding: '8px', boxShadow: '0 0 20px rgba(245, 158, 11, 0.5)'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_coinrec-landing-1/artifacts/0s90425d_MOBILE-APP-LINKS%202025-10-07.png"
                  alt="Download CoinRec App"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="qr-text">
                <p className="text-sm text-slate-300 font-medium text-center">Scan to Download</p>
                <p className="text-xs text-slate-400 text-center">Detects your device automatically</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support / Questions Section */}
      <section id="support" className="py-16 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#1B222A'}}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Support & Questions
            </h2>
            <p className="text-xl text-slate-300">
              Have questions about CoinRec? Need help with coin identification? We're here to assist you.
            </p>
          </div>

          <Card className="border-slate-700" style={{backgroundColor: 'rgba(21, 26, 31, 0.5)'}}>
            <CardContent className="p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                      Name *
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={contactForm.name}
                      onChange={handleContactChange('name')}
                      required
                      disabled={isSubmitting}
                      className="border-slate-600 text-white placeholder-slate-400" style={{backgroundColor: '#1B222A'}}
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={handleContactChange('email')}
                      required
                      disabled={isSubmitting}
                      className="border-slate-600 text-white placeholder-slate-400" style={{backgroundColor: '#1B222A'}}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-300 mb-2">
                    Subject *
                  </label>
                  <Input
                    id="subject"
                    type="text"
                    value={contactForm.subject}
                    onChange={handleContactChange('subject')}
                    required
                    disabled={isSubmitting}
                    className="border-slate-600 text-white placeholder-slate-400" style={{backgroundColor: '#1B222A'}}
                    placeholder="Brief description of your question or issue"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-slate-300 mb-2">
                    Category *
                  </label>
                  <Select value={contactForm.category} onValueChange={(value) => setContactForm(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger className="border-slate-600 text-white" style={{backgroundColor: '#1B222A'}}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="border-slate-600" style={{backgroundColor: '#1B222A'}}>
                      <SelectItem value="suggestions" className="text-white" style={{'&:hover': {backgroundColor: '#151A1F'}}}>Suggestions</SelectItem>
                      <SelectItem value="problem-with-data" className="text-white" style={{'&:hover': {backgroundColor: '#151A1F'}}}>Problem with data</SelectItem>
                      <SelectItem value="bug-report" className="text-white" style={{'&:hover': {backgroundColor: '#151A1F'}}}>Bug report</SelectItem>
                      <SelectItem value="other" className="text-white" style={{'&:hover': {backgroundColor: '#151A1F'}}}>Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    value={contactForm.message}
                    onChange={handleContactChange('message')}
                    required
                    disabled={isSubmitting}
                    rows={5}
                    className="border-slate-600 text-white placeholder-slate-400 resize-none" style={{backgroundColor: '#1B222A'}}
                    placeholder="Please describe your question, issue, or suggestion in detail..."
                  />
                </div>

                <div className="flex flex-col items-center space-y-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold px-8 py-3 rounded-xl transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  {submitMessage && (
                    <div className={`relative transition-all duration-500`}>
                      {submitMessage.includes('Thank you') ? (
                        <div className="relative success-bounce">
                          {/* Subtle glow background - no constant animation */}
                          <div className="absolute inset-0 bg-gradient-to-r from-green-400/10 to-emerald-400/10 blur-lg rounded-xl"></div>
                          
                          {/* Smaller, cleaner success card */}
                          <div className="relative bg-gradient-to-r from-green-500/5 to-emerald-500/5 border border-green-400/20 rounded-xl p-4 backdrop-blur-sm shadow-lg animate-in slide-in-from-bottom-4">
                            {/* Close button */}
                            <button
                              onClick={() => setSubmitMessage('')}
                              className="absolute top-2 right-2 text-green-300 hover:text-green-100 transition-colors p-1 rounded-full hover:bg-green-400/10"
                              aria-label="Close message"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            
                            {/* Success icon with single bounce - no constant animation */}
                            <div className="flex items-center justify-center mb-3">
                              <CheckCircle className="w-7 h-7 text-green-400 success-bounce" />
                            </div>
                            
                            {/* Cleaner success text */}
                            <div className="text-center pr-6">
                              <h4 className="text-green-400 font-semibold text-base mb-2 tracking-wide">
                                MESSAGE SENT
                              </h4>
                              <div className="space-y-1">
                                <p className="text-green-300 text-sm font-medium">
                                  Thank you for your message!
                                </p>
                                <p className="text-green-200 text-xs opacity-90">
                                  We will get back to you soon.
                                </p>
                              </div>
                            </div>
                            
                            {/* Single progress animation - stops after completion */}
                            <div className="mt-4 relative h-1 bg-green-900/20 rounded-full overflow-hidden">
                              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full tech-progress-once" style={{width: '100%'}}></div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-red-500/10 border border-red-400/30 rounded-xl p-4 backdrop-blur-sm animate-in slide-in-from-bottom-4">
                          <div className="flex items-center justify-center space-x-3 text-red-400">
                            <div className="w-5 h-5 rounded-full border-2 border-red-400 flex items-center justify-center">
                              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                            </div>
                            <span className="text-sm font-medium">{submitMessage}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer - Proper 3-Column Layout */}
      <footer className="text-white py-12 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#151A1F'}}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info with App Icon */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <KitcoLogo className="h-6" />
                <div className="w-px h-8 bg-slate-700"></div>
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_coinrec-landing-1/artifacts/4jez9dy6_434x0w.webp"
                    alt="CoinRec App"
                    className="w-10 h-10 rounded-lg shadow-md"
                  />
                  <div>
                    <div className="text-white font-semibold text-sm">CoinRec</div>
                    <div className="text-slate-400 text-xs">by Kitco</div>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 mb-6 max-w-lg">
                Instantly discover the precious metal value of your coins with live market prices for silver, gold, and other metals.
              </p>
            </div>
            
            {/* Company Links */}
            <div>
              <h3 className="font-semibold mb-4 text-white">Company</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="https://corp.kitco.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">About Kitco</a></li>
                <li><a href="https://www.kitco.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">Privacy Policy</a></li>
                <li><a href="https://www.kitco.com/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">Terms of Use</a></li>
                <li><a href="mailto:support@kitco.com" className="hover:text-amber-400 transition-colors">Support</a></li>
              </ul>
            </div>
            
            {/* More Kitco Apps - Separate Column */}
            <div>
              <h3 className="font-semibold mb-4 text-white">More Kitco Apps</h3>
              <div className="space-y-2">
                <a 
                  href="https://apps.apple.com/ca/developer/kitco-metals-inc/id298687561"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-slate-400 hover:text-amber-400 transition-colors group"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_coinrec-landing-1/artifacts/2l20w1dp_app_store.svg"
                    alt="App Store"
                    className="w-5 h-5 group-hover:scale-110 transition-transform"
                  />
                  <span className="text-sm">App Store</span>
                </a>
                
                <a 
                  href="https://play.google.com/store/apps/developer?id=Kitco+Metals+Inc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-slate-400 hover:text-green-400 transition-colors group"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_coinrec-landing-1/artifacts/cbuelwv7_ggplay_store.svg"
                    alt="Google Play"
                    className="w-5 h-5 group-hover:scale-110 transition-transform"
                  />
                  <span className="text-sm">Google Play</span>
                </a>
              </div>
            </div>
          </div>
          
          <Separator className="my-8 bg-slate-800" />
          <div className="flex justify-center">
            <p className="text-slate-400 text-sm">
              © 2025 Kitco Metals Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;